import React, { useState } from 'react';
// material-ui
import { useTheme } from '@mui/material/styles';
import {
    Button,
    CardContent,
    Grid,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Tooltip,
    Pagination,
    IconButton, InputLabel, Select, MenuItem, FormControl,
    TextField,
    Autocomplete,
    OutlinedInput,
    TableSortLabel,
    CircularProgress
} from '@mui/material';
import LibraryAddOutlinedIcon from '@mui/icons-material/LibraryAddOutlined';
import Loader from 'ui-component/Loader';
import BorderColorIcon from '@mui/icons-material/BorderColor';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';

// project imports
import MainCard from 'ui-component/cards/MainCard';
import { gridSpacing } from 'store/constant';
import { useNavigate, useParams } from 'react-router-dom';
import useGetProjectActivityList from 'hooks/consume_api/query/useGetProjectActivityList';
import { ProjectActivityType } from 'types/sacp';
import DeleteTwoToneIcon from '@mui/icons-material/DeleteTwoTone';
import useAuth from 'hooks/useAuth';
import useSnackbar from 'hooks/useSnackbar';
import useDeleteProject from 'hooks/consume_api/mutation/useDeleteProject';
import PromptDialog from 'views/ui-element/PromptDialog';
import BorderColorTwoToneIcon from '@mui/icons-material/BorderColorTwoTone';
import DeleteDeny from './utils/DeleteDeny';
import useGetLocationList from 'hooks/consume_api/query/useGetLocationList';
import { initiateProjectExport } from 'services/reactQueryservices';
import SearchIcon from '@mui/icons-material/Search';
import useGetSubActivityList from 'hooks/consume_api/query/useGetSubActivityList';
import { isExportInProgress, runTrackedExport, subscribeToExportStatus } from '../exportStatus';

interface FieldOption {
    id: number;
    field_name: string;
    field_parent_id: number;
    field_type_id: number;
    geocode: string;
    geojson: object;
    uploaded_file_path: string;
    created_by: number;
    updated_by: number;
    created_date: string;
    updated_date: string;
    longitude: number | null;
    latitude: number | null;
    field_name_bn: string | null;
}
const ProjectActivityMain = () => {
    const { id: projectId } = useParams();
    const submissionUrl = `/project/${projectId}/form/160/538/new-submission`;
    const editUrl = `/project/${projectId}/form/160/538/edit-submission`;
    const profileUrl = `/project/${projectId}/sacp/project-activity/profile`;
    const navigate = useNavigate();
    const theme = useTheme();
    const { user } = useAuth();
    const snackBar = useSnackbar();
    const isMountedRef = React.useRef(true);

    const [location, setLocation] = useState<string>('-1');
    const [divisionId, setDivisionId] = useState<string>('');
    const [districtId, setDistrictId] = useState<string>('');
    const [upazilaId, setUpazilaId] = useState<string>('');

    const [div, setDiv] = useState<string>('');
    const [dis, setDis] = useState<string>('');
    const [upa, setUpa] = useState<string>('');

    const { data: divisionData } = useGetLocationList(location);
    const { data: distirctData } = useGetLocationList(div);
    const { data: upazilaData } = useGetLocationList(dis);


    const [open, setOpen] = useState(false);
    const [deleteId, setDeleteId] = React.useState('');
    const [page, setPage] = useState<number>(1);
    const [row, setRow] = useState<number>(30);
    const [search, setSearch] = useState<string>('');
    const [jumpPage, setJumpPage] = useState<string>('');
    const [finalSearch, setFinalSearch] = useState<string>('');

    const [projectType, setProjectType] = useState<string>('');
    const [subActivity, setSubActivity] = useState<string>('');
    const [startDate, setStartDate] = useState<string>('');
    const [endDate, setEndDate] = useState<string>('');
    const [sortBy, setSortBy] = useState<string>('id');
    const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
    const [isExporting, setIsExporting] = useState<boolean>(false);
    const projectActivityExportStatusKey = `sacp-project-activity-export:${projectId || ''}`;

    const { data: subActivityData } = useGetSubActivityList(projectType);

    React.useEffect(() => {
        isMountedRef.current = true;

        return () => {
            isMountedRef.current = false;
        };
    }, []);

    // Sync sorting with backend logic: if sorting by date (backend default when filtered), update UI state
    React.useEffect(() => {
        if (startDate || endDate) {
            setSortBy('implementation_date');
            setSortOrder('asc');
        } else {
            setSortBy('id');
            setSortOrder('desc');
        }
    }, [startDate, endDate]);

    React.useEffect(() => {
        const syncExportStatus = () => {
            if (isMountedRef.current) {
                setIsExporting(isExportInProgress(projectActivityExportStatusKey));
            }
        };

        syncExportStatus();

        return subscribeToExportStatus(projectActivityExportStatusKey, syncExportStatus);
    }, [projectActivityExportStatusKey]);

    const { data, isFetching } = useGetProjectActivityList(page, row, finalSearch, divisionId, districtId, upazilaId, projectType, subActivity, startDate, endDate);

    const handleRequestSort = (property: string) => {
        const isAsc = sortBy === property && sortOrder === 'asc';
        setSortOrder(isAsc ? 'desc' : 'asc');
        setSortBy(property);
    };

    const sortedData = React.useMemo(() => {
        if (!data?.data?.data) return [];
        const items = [...data.data.data];
        return items.sort((a, b) => {
            let valA = a[sortBy as keyof ProjectActivityType] || '';
            let valB = b[sortBy as keyof ProjectActivityType] || '';

            if (sortBy === 'implementation_date') {
                valA = valA ? new Date(valA).getTime() : 0;
                valB = valB ? new Date(valB).getTime() : 0;
            }

            if (valA < valB) return sortOrder === 'asc' ? -1 : 1;
            if (valA > valB) return sortOrder === 'asc' ? 1 : -1;
            return 0;
        });
    }, [data, sortBy, sortOrder]);

    const { mutate } = useDeleteProject();

    const handleAddClick = () => {
        navigate(submissionUrl);
    };

    const handleUpdate = (id: number) => {
        navigate(`${editUrl}/${id}`);
    };

    const handleProfile = (subActivity: string, subActivityId: string, date: string | null, location: string, instance: number, implementedBy: string) => {
        navigate(`${profileUrl}?subactivity=${subActivity}&subactivityId=${subActivityId}&date=${date}&location=${location}&instance=${instance}&implementedby=${implementedBy}`);
    };
    const handleChangePage = (_: React.ChangeEvent<unknown>, newPage: number) => {
        setPage(newPage);
    };

    const handleSearch = () => {
        setFinalSearch(search);
    };

    const handleSearchKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            handleSearch();
        }
    };


    const handleDelete = (id: number | string) => {
        let admin = false;
        user?.roles?.map((role) => {
            if (role.name === "FAO_ADMIN") {
                admin = true;
            }
        })
        if (admin) {
            setDeleteId(id.toString());
            setOpen(true);
        }
        else {
            snackBar("Only admin can delete");
        }
    }

    const handleExport = async (indicator: number) => {
        if (isExportInProgress(projectActivityExportStatusKey)) {
            setIsExporting(true);
            return;
        }

        try {
            setIsExporting(true);
            await runTrackedExport(projectActivityExportStatusKey, () =>
                initiateProjectExport(divisionId, districtId, upazilaId, finalSearch, indicator, projectType, subActivity, startDate, endDate)
            );
        } catch (error) {
            if (isMountedRef.current) {
                snackBar('Export failed. Please try again.', 'error');
            }
        } finally {
            if (isMountedRef.current) {
                setIsExporting(isExportInProgress(projectActivityExportStatusKey));
            }
        }
    }
    return (
        <>

            <MainCard
                contentSX={{ padding: 0 }}
                sx-={{ padding: 0 }}
                title={
                    <Grid container alignItems="center" justifyContent="space-between" spacing={gridSpacing}>
                        <Grid item>
                            <Typography variant="h3">Project Activity List</Typography>
                        </Grid>
                        <Grid item display="flex" justifyContent="space-between" >
                            <Button
                                sx={{ minWidth: "150px" }}
                                color="secondary"
                                variant="contained"
                                onClick={() => handleAddClick()}
                            >
                                <LibraryAddOutlinedIcon fontSize="small" sx={{ mr: 0.75 }} />
                                Add Project Activity
                            </Button>
                            <FormControl sx={{ ml: 1, minWidth: "170px" }} variant="outlined" disabled={isExporting}>
                                <InputLabel htmlFor="dropdown">{isExporting ? 'Preparing CSV...' : 'Export option'}</InputLabel>
                                <Select
                                    input={<OutlinedInput label={isExporting ? 'Preparing CSV...' : 'Export option'} id="dropdown" />}
                                >
                                    <MenuItem onClick={() => handleExport(1)} value="option1">Activity report</MenuItem>
                                    <MenuItem onClick={() => handleExport(2)} value="option2">Capacity building</MenuItem>
                                </Select>
                            </FormControl>
                            {isExporting && <CircularProgress color="secondary" size={24} sx={{ ml: 1, mt: 1.5 }} />}

                        </Grid>
                    </Grid>
                }
            >
                {isFetching ? (
                    <Loader />
                ) : (
                    <CardContent>
                        <Grid container spacing={2} marginBottom="15px">
                            <Grid item lg={3} sm={6}>
                                <Autocomplete
                                    options={[
                                        { label: 'SACP', value: 'SACP' },
                                        { label: 'RAINS', value: 'RAINS' }
                                    ]}
                                    getOptionLabel={(option) => option.label}
                                    value={projectType ? { label: projectType, value: projectType } : null}
                                    onChange={(event, value) => {
                                        if (value) {
                                            setProjectType(value.value);
                                            setSubActivity(''); // Reset sub-activity when project type changes
                                        } else {
                                            setProjectType('');
                                            setSubActivity('');
                                        }
                                    }}
                                    renderInput={(params) => <TextField {...params} label="Select Project" variant="outlined" />}
                                />
                            </Grid>
                            <Grid item lg={3} sm={6}>
                                <Autocomplete
                                    options={subActivityData?.data || []}
                                    getOptionLabel={(option: any) => option.sub_activity_type_name || option.field_name || option.name || ''}
                                    value={subActivityData?.data?.find((item: any) => (item.sub_activity_type_name || item.field_name || item.name || '') === subActivity) || null}
                                    onChange={(event, value: any) => {
                                        if (value) {
                                            setSubActivity(value.sub_activity_type_name || value.field_name || value.name || '');
                                        } else {
                                            setSubActivity('');
                                        }
                                    }}
                                    disabled={!projectType}
                                    renderInput={(params) => <TextField {...params} label="Sub-activity" variant="outlined" />}
                                />
                            </Grid>
                            <Grid item lg={3} sm={6}>
                                <TextField
                                    type="date"
                                    label="Start Date"
                                    value={startDate}
                                    onChange={(e) => setStartDate(e.target.value)}
                                    InputLabelProps={{
                                        shrink: true,
                                    }}
                                    fullWidth
                                    variant="outlined"
                                />
                            </Grid>
                            <Grid item lg={3} sm={6}>
                                <TextField
                                    type="date"
                                    label="End Date"
                                    value={endDate}
                                    onChange={(e) => setEndDate(e.target.value)}
                                    InputLabelProps={{
                                        shrink: true,
                                    }}
                                    fullWidth
                                    variant="outlined"
                                />
                            </Grid>
                        </Grid>
                        <Grid container spacing={2} marginBottom="15px">
                            <Grid item lg={3} sm={6}>
                                <Autocomplete
                                    options={divisionData?.data as FieldOption[]}
                                    getOptionLabel={(option) => option.field_name}
                                    value={divisionData?.data.find((item: any) => item.geocode.toString() === divisionId) || null}
                                    onChange={(event, value) => {
                                        if (value) {
                                            setDivisionId(value?.geocode.toString());
                                            setDiv(value?.id.toString());
                                            setDis("");
                                            setUpa("");
                                            setDistrictId('');
                                            setUpazilaId('');
                                        }
                                    }}
                                    renderInput={(params) => <TextField {...params} label="Select division" variant="outlined" />}
                                />
                            </Grid>
                            <Grid item lg={3} sm={6}>
                                <Autocomplete
                                    options={(distirctData?.data as FieldOption[]) ?? []}
                                    getOptionLabel={(option) => option.field_name}
                                    value={distirctData?.data.find((item: any) => item.geocode.toString() === districtId) || null}
                                    onChange={(event, value) => {
                                        if (value) {
                                            setDistrictId(value?.geocode.toString());
                                            setDis(value?.id.toString());
                                            setUpa("");
                                            setUpazilaId('');
                                        }
                                    }}
                                    renderInput={(params) => <TextField {...params} label="Select district" variant="outlined" />}
                                />
                            </Grid>
                            <Grid item lg={3} sm={6}>
                                <Autocomplete
                                    options={(upazilaData?.data as FieldOption[]) ?? []}
                                    getOptionLabel={(option) => option.field_name}
                                    value={upazilaData?.data.find((item: any) => item.geocode.toString() === upazilaId) || null}
                                    onChange={(event, value) => {
                                        if (value) {
                                            setUpazilaId(value?.geocode.toString());
                                            setUpa(value?.id.toString());
                                        }
                                    }}
                                    renderInput={(params) => <TextField {...params} label="Select upazila" variant="outlined" />}
                                />
                            </Grid>
                        </Grid>
                        <Grid container justifyContent="flex-end" alignItems="center" spacing={1}>
                            <Grid item>
                                <TextField
                                    onChange={(e) => setSearch(e.target.value)}
                                    onKeyDown={handleSearchKeyDown}
                                    value={search}
                                    id="outlined-basic"
                                    label="search"
                                    variant="outlined"
                                />
                            </Grid>
                            <Grid item>
                                <IconButton onClick={handleSearch} color="primary">
                                    <SearchIcon />
                                </IconButton>
                            </Grid>
                        </Grid>

                        <TableContainer component={Paper}>
                            <Table sx={{ width: '100%' }} aria-label="simple table">
                                <TableHead>
                                    <TableRow>
                                        <TableCell align="left">#</TableCell>
                                        <TableCell align="left">
                                            <TableSortLabel
                                                active={sortBy === 'sub_activity'}
                                                direction={sortBy === 'sub_activity' ? sortOrder : 'asc'}
                                                onClick={() => handleRequestSort('sub_activity')}
                                            >
                                                Name of Sub-Activity
                                            </TableSortLabel>
                                        </TableCell>
                                        <TableCell align="left">Implemented By</TableCell>
                                        <TableCell align="left">
                                            <TableSortLabel
                                                active={sortBy === 'implementation_date'}
                                                direction={sortBy === 'implementation_date' ? sortOrder : 'asc'}
                                                onClick={() => handleRequestSort('implementation_date')}
                                            >
                                                Date
                                            </TableSortLabel>
                                        </TableCell>
                                        <TableCell align="left">Location</TableCell>
                                        <TableCell align="left">Member Count</TableCell>
                                        <TableCell align="left">Action</TableCell>
                                        <TableCell align="left">Validation Status</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {sortedData.map((item: any, index: number) => (
                                        <TableRow hover key={item.id}>
                                            <TableCell align="left">{(page - 1) * row + index + 1}</TableCell>
                                            <TableCell align="left">{item.sub_activity}</TableCell>
                                            <TableCell align="left">{item.implemented_by}</TableCell>
                                            <TableCell align="left">{item.implementation_date}</TableCell>
                                            <TableCell align="left">{item.location}</TableCell>
                                            <TableCell align="left">{item.member_count}</TableCell>
                                            <TableCell align="left">
                                                <Grid sx={{ display: 'flex', gap: '5px' }}>
                                                    {item.is_validate !== true && (
                                                        <Tooltip placement="top" title="Edit">
                                                            <IconButton
                                                                color="primary"
                                                                sx={{
                                                                    color: theme.palette.warning.dark,
                                                                    borderColor: theme.palette.warning.main,
                                                                    '&:hover ': { background: theme.palette.warning.light }
                                                                }}
                                                                size="large"
                                                                onClick={() => {
                                                                    // Navigate to edit or handle update
                                                                    navigate(`${editUrl}/${item.id}`);
                                                                }}
                                                            >
                                                                <BorderColorTwoToneIcon sx={{ fontSize: '1.3rem' }} />
                                                            </IconButton>
                                                        </Tooltip>
                                                    )}

                                                    <Tooltip title="Click to view profile" arrow>
                                                        <IconButton
                                                            color="success"
                                                            onClick={() =>
                                                                handleProfile(
                                                                    item.sub_activity,
                                                                    item.sub_activity_type,
                                                                    item.implementation_date,
                                                                    item.location,
                                                                    item.id,
                                                                    item.implemented_by
                                                                )
                                                            }
                                                            size="large"
                                                        >
                                                            <PersonOutlineIcon />
                                                        </IconButton>
                                                    </Tooltip>

                                                    {item.is_validate !== true && (
                                                        <Tooltip placement="top" title="Delete">
                                                            <IconButton
                                                                color="error"
                                                                onClick={() => handleDelete(item.id)}
                                                                size="large"
                                                            >
                                                                <DeleteTwoToneIcon />
                                                            </IconButton>
                                                        </Tooltip>
                                                    )}
                                                </Grid>
                                            </TableCell>
                                            <TableCell align="left">
                                                {item.project_id && 'Rejected'}
                                                {item.is_validate && 'Approved'}
                                                {(item.is_validate !== true && !item.project_id) && 'Pending'}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table >
                            <Grid container spacing={2} alignItems="center" marginTop="10px">
                                <Grid item>
                                    <Typography>Total Project: {data?.data?.total}</Typography>
                                    <Typography>Total Member: {data?.data?.totalMemberCount}</Typography>
                                </Grid>
                                <Grid item>
                                    <TextField
                                        select
                                        label="Rows per page"
                                        value={row}
                                        onChange={(e) => setRow(Number(e.target.value))}
                                        sx={{ width: '120px' }}
                                    >
                                        <MenuItem value={25}>25</MenuItem>
                                        <MenuItem value={30}>30</MenuItem>
                                        <MenuItem value={100}>100</MenuItem>
                                    </TextField>
                                </Grid>
                                <Grid item>
                                    <TextField
                                        type="number"
                                        label="Go to page"
                                        value={jumpPage}
                                        onChange={(e) => setJumpPage(e.target.value)}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter') {
                                                const p = parseInt(jumpPage);
                                                const maxPage = Math.ceil(data?.data?.total / row);
                                                if (p >= 1 && p <= maxPage) {
                                                    setPage(p);
                                                    setJumpPage('');
                                                }
                                            }
                                        }}
                                        inputProps={{ min: 1, max: Math.ceil(data?.data?.total / row) }}
                                        sx={{ width: '120px' }}
                                    />
                                </Grid>
                                <Grid item>
                                    <Pagination
                                        count={Math.ceil(data?.data?.total / row)}
                                        color="primary"
                                        page={page}
                                        onChange={handleChangePage}
                                    />
                                </Grid>
                            </Grid>
                        </TableContainer >
                    </CardContent >
                )}

                <PromptDialog
                    open={open}
                    setOpen={setOpen}
                    agreeAction={() => mutate(deleteId)}
                    dialogTitleText={'Remove participant'}
                    dialogContentText={'This participant will be removed. Are you sure?'}
                    disagreeText={'Cancel'}
                    agreeText={'Remove'}
                />
            </MainCard >
        </>
    );
};

export default ProjectActivityMain;
